import React, { useState, useCallback } from 'react';

import {
  aiFormattedPageContext,
  ElementMapping,
  AIFormattedPageContext,
  getSelectorFromIndex,
} from '~hooks/prompt/aiFormatter';

import { CommandAnswer, ChatResponse, sendMessageToConversation, BrowserTask } from './api';
import { performElementActionAndWait } from './pageActions';

interface UseConversationReturn {
  prompt: string;
  output: string;
  isLoading: boolean;
  isExecutingAction: boolean;
  setPrompt: (prompt: string) => void;
  handleSubmit: () => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
}

export const useConversation = (
  conversationId?: string,
  companyId?: string,
): UseConversationReturn => {
  const [prompt, setPrompt] = useState('');
  const [output, setOutput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExecutingAction, setIsExecutingAction] = useState(false);
  const [stepNumber, setStepNumber] = useState(0);

  // 출력 업데이트 헬퍼 함수들
  const appendOutput = (text: string) => {
    setOutput((prev) => prev + text);
  };

  const clearOutput = () => {
    setOutput('');
  };

  const resetConversation = () => {
    clearOutput();
    setStepNumber(0);
  };

  const executeBrowserActions = async (
    actions: BrowserTask['actions'],
    elementMappings: ElementMapping[],
  ) => {
    for (const action of actions) {
      console.debug(action);
      console.debug(elementMappings);

      const { type, id, value } = action;
      try {
        // AI 응답의 id는 항상 인덱스이므로 변환
        let selector: string | undefined;
        if (id) {
          selector = getSelectorFromIndex(parseInt(id), elementMappings) || undefined;
        }

        await performElementActionAndWait(selector, type, value);
      } catch (error) {
        console.error(`Failed to execute ${type} on index ${id}:`, error);
        throw new Error(`액션 수행 실패: ${type} on index ${id}`);
      }
    }
  };

  // 브라우저 작업 실행 (elementMappings를 매개변수로 받음)
  const executeBrowserTask = async (task: BrowserTask, elementMappings: ElementMapping[]) => {
    const { nextGoal, taskComplete, actions } = task;

    setIsExecutingAction(true);

    try {
      // 액션들을 순차적으로 실행
      await executeBrowserActions(actions, elementMappings);

      // 결과 처리
      if (taskComplete) {
        setStepNumber(0); // 작업 완료 시 스텝 번호 리셋
        return { taskComplete: true };
      } else {
        const nextStep = stepNumber + 1;
        setStepNumber(nextStep);
        return { taskComplete: false, nextStep };
      }
    } catch (error) {
      throw error;
    } finally {
      setIsExecutingAction(false);
    }
  };

  const executeCommand = async (
    commandAnswer: CommandAnswer,
    elementMappings: ElementMapping[],
  ) => {
    try {
      if (commandAnswer.performBrowserTask) {
        const result = await executeBrowserTask(commandAnswer.performBrowserTask, elementMappings);

        // 작업이 완료되지 않았으면 자동으로 다음 단계 요청
        if (!result.taskComplete && result.nextStep) {
          await sendContinueRequest(result.nextStep);
        }
      }
    } catch (error) {
      console.error('Error executing command:', error);
    }
  };

  const sendMessage = async (content: string, pageContextData: AIFormattedPageContext) => {
    if (!companyId || !conversationId) {
      throw new Error('Company ID와 Conversation ID가 필요합니다.');
    }

    setIsLoading(true);

    try {
      const request = {
        conversationId,
        content,
        currentCompanyId: companyId,
        pageContext: pageContextData.context,
      };

      await sendMessageToConversation(request, {
        onMessage: async (data: ChatResponse) => {
          if (data.plainTextAnswer?.text) {
            appendOutput(data.plainTextAnswer.text);
          }

          if (data.commandAnswer) {
            // elementMappings를 직접 전달
            await executeCommand(data.commandAnswer, pageContextData.elementMappings);
          }
        },
        onClose: () => {
          setIsLoading(false);
        },
        onError: (error: unknown) => {
          console.error('Error in conversation:', error);
          setIsLoading(false);
        },
      });
    } catch (error) {
      console.error('Error sending message:', error);
      setIsLoading(false);
    }
  };

  // 연속 요청 보내기
  const sendContinueRequest = async (step: number) => {
    if (!companyId || isLoading || !conversationId) return;

    const continueMessage = `continue_perform_action:${step}`;
    appendOutput(`\n💬 ${continueMessage}`);

    try {
      const pageContextData = aiFormattedPageContext();
      await sendMessage(continueMessage, pageContextData);
    } catch (error) {
      console.error('Error sending continue request:', error);
    }
  };

  // 제출 처리
  const handleSubmit = useCallback(async () => {
    if (!prompt.trim() || isLoading || !companyId) return;

    setIsLoading(true);
    resetConversation();

    try {
      const pageContextData = aiFormattedPageContext();
      await sendMessage(prompt, pageContextData);
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
    }
  }, [prompt, isLoading, companyId]);

  // 키보드 이벤트 처리
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  return {
    prompt,
    output,
    isLoading,
    isExecutingAction,
    setPrompt,
    handleSubmit,
    handleKeyPress,
  };
};
